import { cn } from '@playerbase/ui';

import { FeedbackRow } from './feedback-row.js';
import { pollSourceLabel, pollVoteDetailFor } from './poll-activity.js';
import {
  activityRowFor,
  type InboxGroup,
  type InboxMessage,
} from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Compact activity-panel entry.
//
// Renders only the "activity-driving" InboxRow for the group (the one
// whose lastActivityAt drove the bump) plus a small contextual label
// describing where to find it on the feed side. Forum / chat threads
// no longer flash up their entire history here - that view of "the
// new message in [thread]" is exactly what makes the right column
// useful as a triage list rather than a duplicate feed.
//
// Click delegates to the parent: the inbox container scrolls the feed
// to the matching group, expands it if necessary, and pulses a ring
// on the inner row. We do that work via callbacks rather than routing
// because the feed is on the same page - a router push would unmount
// the activity panel and lose context.
// ---------------------------------------------------------------------------

interface ActivityItemProps {
  group: InboxGroup;
  byExternalId: Map<string, InboxMessage>;
  onSelect: () => void;
  unread: boolean;
}

function sourceLabelFor(group: InboxGroup): string {
  const head = group.head;
  const pollLabel = pollSourceLabel(group);
  if (pollLabel !== null) return pollLabel;
  if (group.kind === 'forum-post' || group.kind === 'thread') {
    // For forum posts the head's discordChannelName IS the thread
    // title (Discord stores the thread name on the channel column).
    // For chat-spawned threads, the head sits in a regular channel,
    // so we surface the thread channel name from a reply instead -
    // the user wants the source to be the thread, not the parent.
    if (group.kind === 'thread') {
      const replyWithName = group.replies.find(
        (r) => r.discordChannelName !== null,
      );
      const threadName =
        replyWithName?.discordChannelName ?? head.discordChannelName ?? 'thread';
      return `in thread ${threadName}`;
    }
    const threadName = head.discordChannelName ?? '(thread)';
    if (head.discordParentChannelName !== null) {
      return `in ${threadName} · #${head.discordParentChannelName}`;
    }
    return `in ${threadName}`;
  }
  // Replies whose parent is a poll are kept as their own top-level
  // 'single' group (so the reply surfaces in the message feed instead
  // of being buried under the poll). The label still reads as a
  // reply, since semantically that's what it is.
  if (group.kind === 'reply-chain' || head.replyToExternalId !== null) {
    return `reply in #${head.discordChannelName ?? '(channel)'}`;
  }
  return `in #${head.discordChannelName ?? '(channel)'}`;
}

export function ActivityItem({
  group,
  byExternalId,
  onSelect,
  unread,
}: ActivityItemProps): JSX.Element {
  const row = activityRowFor(group);
  const label = sourceLabelFor(group);
  const voteDetail = pollVoteDetailFor(group);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'block cursor-pointer transition-colors',
        // Hover treatment is a single surface change across the whole
        // tile (source label + row body). Surface-3 is the next step
        // up from the Card's own bg-surface-2 - using surface-2 here
        // would be invisible against the panel background. The
        // cascade extends the hover state into FeedbackRow's outer
        // wrapper so hovering anywhere on the tile (including the
        // source-label band) shifts the whole row in step.
        'hover:bg-surface-3 [&:hover_>div:last-child]:bg-surface-3',
        'focus:outline-none focus-visible:bg-surface-3',
      )}
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-2 text-xs text-text-muted">
        <span className="truncate">{label}</span>
        {unread && (
          <span
            aria-hidden
            className="block h-2 w-2 shrink-0 rounded-full bg-accent motion-safe:animate-glow"
          />
        )}
      </div>
      <FeedbackRow
        row={row}
        byExternalId={byExternalId}
        hideChannelLabel
        {...(voteDetail !== null ? { pollVoteDetail: voteDetail } : {})}
      />
    </div>
  );
}
